function debounce(fn, wait = 150) {
	let t; return (...a) => {
		clearTimeout(t); t = setTimeout(() => fn(...a), wait);
	};
}
const getLineHeightPx = (el) => {
	const cs = getComputedStyle(el);
	const fs = parseFloat(cs.fontSize) || 16;
	const lh = cs.lineHeight;
	return (lh === 'normal' ? fs * 1.2 : parseFloat(lh)) || (fs * 1.2);
};

const getLines = (wrap, contentEl) => {
	const read = (el) => el && el.hasAttribute('data-lines')
		? Math.max(1, parseInt(el.getAttribute('data-lines') || '10', 10))
		: null;
	const fromContent = read(contentEl); if (fromContent) {
		return fromContent;
	}
	const col = contentEl?.closest('.wp-block-column'); const fromCol = read(col); if (fromCol) {
		return fromCol;
	}
	const fromWrap = read(wrap); return fromWrap ?? 10;
};
const computeCollapsedMax = (content, wrap) => getLines(wrap, content) * getLineHeightPx(content);

/**
 * Put .equal-height-column on the wrapper .wp-block-columns element.
 */
export function equalHeightColumns() {
	const wrappers = Array.from(document.querySelectorAll('.equal-height-column.wp-block-columns'));

	if (!wrappers.length) {
		return;
	}

	const prepareWrapper = (wrap) => {
		const cards = Array.from(wrap.querySelectorAll(':scope .wp-block-column'));
		if (!cards.length) {
			return;
		}

		const nearestBgColor = (node) => {
			let el = node;
			while (el && el !== document.documentElement) {
				const bg = getComputedStyle(el).backgroundColor;
				if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') {
					return bg;
				}
				el = el.parentElement;
			}
			return 'rgb(255, 255, 255)'; // fallback
		};
		const rgbTriplet = (bg) => {
			const m = bg.match(/rgba?\s*\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*([0-9.]+))?\s*\)/i);
			return m ? `${ parseFloat(m[ 1 ]) }, ${ parseFloat(m[ 2 ]) }, ${ parseFloat(m[ 3 ]) }` : '255, 255, 255';
		};
		const applyFade = (contentEl) => {
			const fade = contentEl.querySelector(':scope .eqh-fade');
			if (!fade) {
				return;
			}
			const rgb = rgbTriplet(nearestBgColor(contentEl));
			fade.style.background = `linear-gradient(to bottom, rgba(${ rgb }, 0), rgb(${ rgb }))`;
		};

		const cardsInfo = cards.map((card) => {
			card.classList.add('eqh-card');

			const h = card.querySelector(':scope h2, :scope h3, :scope h4, :scope h5, :scope h6');
			let firstContent = null;
			const kids = Array.from(card.children);
			for (const k of kids) {
				if (k === h) {
					continue;
				}
				const tag = k.tagName.toLowerCase();
				if (tag === 'figure') {
					continue;
				}
				firstContent = k; break;
			}

			let content = card.querySelector(':scope .eqh-content');
			if (!content) {
				content = document.createElement('div');
				content.className = 'eqh-content';
				const startIndex = firstContent ? kids.indexOf(firstContent) : -1;
				if (startIndex >= 0) {
					for (let i = startIndex; i < kids.length; i ++) {
						content.appendChild(kids[ i ]);
					}
					card.appendChild(content);
				} else {
					card.appendChild(content);
				}
				const fade = document.createElement('span');
				fade.className = 'eqh-fade';
				fade.setAttribute('aria-hidden', 'true');
				content.appendChild(fade);
			}

			// toggle button
			let toggle = card.querySelector(':scope .eqh-toggle');
			if (!toggle) {
				toggle = document.createElement('button');
				toggle.type = 'button';
				toggle.className = 'eqh-toggle';
				toggle.hidden = true;
				toggle.setAttribute('aria-expanded', 'false');
				toggle.textContent = 'Read more';
				card.appendChild(toggle);
			}

			// clamp init
			const collapsedMax = computeCollapsedMax(content, wrap);
			content.__collapsedMax = collapsedMax;
			content.classList.add('is-collapsed');
			content.style.maxHeight = collapsedMax + 'px';

			// overflow check
			const fadeElMeasure1 = content.querySelector(':scope .eqh-fade');
			const prevFadeDisplay1 = fadeElMeasure1 ? fadeElMeasure1.style.display : null;
			if (fadeElMeasure1) fadeElMeasure1.style.display = 'none';

			const prev = content.style.maxHeight;
			content.style.maxHeight = 'none';
			const fullH = content.scrollHeight;
			content.style.maxHeight = prev;

			if (fadeElMeasure1) fadeElMeasure1.style.display = prevFadeDisplay1 ?? '';

			const overflowing = fullH > collapsedMax + 1;
			toggle.hidden = !overflowing;
			toggle.textContent = 'Read more';
			toggle.setAttribute('aria-expanded', 'false');

			// initial fade
			applyFade(content);

			if (!toggle.__eqhBound) {
				toggle.addEventListener('click', () => {
					const expanded = toggle.getAttribute('aria-expanded') === 'true';
					const cm = content.__collapsedMax ?? computeCollapsedMax(content, wrap);
					const fadeEl = content.querySelector(':scope .eqh-fade');
					const overflowingNow = content.__overflowing === true;

					if (expanded) {
						// collapse
						content.classList.add('is-collapsed');
						content.style.maxHeight = cm + 'px';
						if (fadeEl) {
							fadeEl.style.display = overflowingNow ? '' : 'none';
						}
						toggle.textContent = 'Read more';
						toggle.setAttribute('aria-expanded', 'false');
					} else {
						// expand
						content.classList.remove('is-collapsed');
						content.style.maxHeight = 'none';
						if (fadeEl) {
							fadeEl.style.display = 'none';
						}
						toggle.textContent = 'Read less';
						toggle.setAttribute('aria-expanded', 'true');
					}
					equalise();
				});
				toggle.__eqhBound = true;
			}

			return { card, content, toggle };
		});

		// equalise heights across this wrapper
		const equalise = () => {
			const states = cardsInfo.map(({ content, toggle }) => {
				const cm = content.__collapsedMax ?? computeCollapsedMax(content, wrap);
				const wasExpanded = toggle && toggle.getAttribute('aria-expanded') === 'true';
				if (wasExpanded) {
					content.classList.add('is-collapsed');
					content.style.maxHeight = cm + 'px';
				}
				return { content, toggle, wasExpanded };
			});

			cards.forEach((c) => c.style.minHeight = '');
			let maxH = 0;
			cards.forEach((c) => {
				const h = c.getBoundingClientRect().height; if (h > maxH) {
					maxH = h;
				}
			});
			cards.forEach((c) => {
				c.style.minHeight = Math.ceil(maxH) + 'px';
			});

			states.forEach(({ content, toggle, wasExpanded }) => {
				if (wasExpanded) {
					content.classList.remove('is-collapsed');
					content.style.maxHeight = 'none';
					toggle.setAttribute('aria-expanded', 'true');
					toggle.textContent = 'Read less';
				}
			});
		};

		const recalc = () => {
			cardsInfo.forEach(({ content, toggle }) => {
				const collapsedMax = computeCollapsedMax(content, wrap);
				content.__collapsedMax = collapsedMax;

				// measure overflow
				const prev = content.style.maxHeight;
				content.style.maxHeight = 'none';
				const fullH = content.scrollHeight;
				content.style.maxHeight = prev;

				const overflowing = fullH > collapsedMax + 1;
				content.__overflowing = overflowing;

				if (toggle.getAttribute('aria-expanded') !== 'true') {
					content.classList.add('is-collapsed');
					content.style.maxHeight = collapsedMax + 'px';
				}
				toggle.hidden = !overflowing;

				// keep fade in sync (only if collapsed & overflowing)
				const fadeEl = content.querySelector(':scope > .eqh-fade');
				if (fadeEl) {
					const isCollapsed = toggle.getAttribute('aria-expanded') !== 'true';
					fadeEl.style.display = (isCollapsed && overflowing) ? '' : 'none';
				}

				applyFade(content);
			});

			requestAnimationFrame(equalise);
		};

		wrap.__eqhEqualise = equalise; // (optional) expose

		recalc();

		// listeners
		const onResize = debounce(recalc, 150);
		window.addEventListener('resize', onResize);

		wrap.querySelectorAll('img').forEach((img) => {
			if (img.complete) {
				return;
			}
			img.addEventListener('load', debounce(recalc, 50), { once: true });
			img.addEventListener('error', debounce(recalc, 50), { once: true });
		});

		if (window.ResizeObserver) {
			const ro = new ResizeObserver(debounce(recalc, 50));
			cardsInfo.forEach(({ content }) => ro.observe(content));
		}
		if (window.MutationObserver) {
			const mo = new MutationObserver(() => recalc());
			mo.observe(wrap, { attributes: true, attributeFilter: ['data-lines'] });
		}
	};

	wrappers.forEach(prepareWrapper);
}
